// @flow

import { useContext, useEffect, useState } from "react";

import type { Schema } from "../../../types/Schema";

import Tooltip from "@kea-mod/common/tooltip";
import { useTranslation } from "react-i18next";
import { ApiContext } from "../../../context/KeaAPI/newApi";
import { SchemaTree } from "../../../types/SchemaTree";

type Props = {
  schemaId: number;
  schemaTree: SchemaTree | undefined;
  closeAction: () => void;
  visible: boolean;
  schema: Schema;
};

export const TreeViewEditSchemaComponent = (props: Props) => {
  const { t } = useTranslation();
  const { schemaApi, schemaTreeApi } = useContext(ApiContext);

  const schema = props.schema;
  if (!schema) throw Error("Unknown schema with id " + props.schemaId + " in TreeViewEditSchemaComponent.");

  // is always undefined if the schema is a root
  const schemaTree = props.schemaTree;

  // schema and schemaTree can be used to reset.
  const [workingSchema, setWorkingSchema] = useState<Schema>(schema);
  const [workingSchemaTree, setWorkingSchemaTree] = useState<SchemaTree | undefined>(schemaTree);

  const handleInputChange = (event: any) => {
    setWorkingSchema({ ...workingSchema, [event.target.name]: event.target.value });
  };

  const handleWeightChange = (event: any) => {
    if (!workingSchemaTree) return;
    setWorkingSchemaTree({ ...workingSchemaTree, [event.target.name]: parseInt(event.target.value, 10) });
  };

  const handleCheckboxChange = (event: any) => {
    setWorkingSchema({ ...workingSchema, [event.target.name]: event.target.checked });
  };

  const updateSchema = () => {
    // only allow updating the name, description, and public status
    let schemaToUpdate: Schema = {
      ...schema,
      name: workingSchema.name,
      description: workingSchema.description,
      publicSchema: workingSchema.publicSchema,
    };
    schemaApi.update(schemaToUpdate);

    // only allow updating the weight
    if (schemaTree && workingSchemaTree) {
      let schemaTreeToUpdate: SchemaTree = { ...schemaTree, weight: workingSchemaTree.weight };
      schemaTreeApi.update(schemaTreeToUpdate);
    }
  };

  // reset the form every time this component is shown
  useEffect(() => {
    if (props.visible) reset();
  }, [props.visible]);

  const reset = () => {
    setWorkingSchema(schema);
    setWorkingSchemaTree(schemaTree);
  };

  return (
    <div className="container mt-6" style={{ display: props.visible ? undefined : "none" }}>
      <div className="section">
        <div className="columns is-centered">
          <div className="column is-three-quarters">
            <div className="box">
              <h4 className="title is-4">{t("schema")}</h4>
              <div className="field">
                <label className="label">{t("name")}</label>
                <input
                  className="input"
                  type="text"
                  name="name"
                  value={workingSchema.name ?? ""}
                  onChange={handleInputChange}
                />
              </div>
              <div className="field">
                <label className="label">
                  {t("description")}
                  <Tooltip
                    icon="circle-info"
                    class="icon tooltip no-border is-white has-tooltip-multiline has-tooltip-top"
                    content={t("descriptionTooltip")}
                  ></Tooltip>
                </label>
                <input
                  className="input"
                  type="text"
                  name="description"
                  value={workingSchema.description ?? ""}
                  onChange={handleInputChange}
                />
              </div>
              {!workingSchemaTree ? (
                <></>
              ) : (
                <div className="field">
                  <label className="label">{t("weight")}</label>
                  <input
                    className="input"
                    type="text"
                    name="weight"
                    value={workingSchemaTree.weight ?? ""}
                    onChange={handleWeightChange}
                  />
                </div>
              )}
              <div className="field">
                <label className="label">{t("schemaispublic")}</label>
                <input
                  name="publicSchema"
                  type="checkbox"
                  checked={workingSchema.publicSchema}
                  onChange={handleCheckboxChange}
                />
              </div>
              <div className="columns mt-5">
                <button
                  className="button is-danger"
                  onClick={() => {
                    reset();
                    props.closeAction();
                  }}
                >
                  {t("cancel")}
                </button>
                <button
                  className="button is-normal is-success"
                  onClick={() => {
                    updateSchema();
                    props.closeAction();
                  }}
                >
                  {t("save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
